import React, { useState } from 'react';
import { Menu } from "lucide-react";
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // List of navigation links
  const navLinks = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'about', label: 'About Us', path: '/about' },
    { id: 'services', label: 'Services', path: '/services' },
    { id: 'portfolio', label: 'Portfolio', path: '/portfolio' },
    { id: 'contact', label: 'Contact Us', path: '/contact' },
  ];

  // Determine if we're on the current page
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // The hidden profile page must keep its original layout (no visible header bar),
  // but it still keeps the language switcher accessible in the top-right corner.
  const isHiddenProfile = location.pathname === '/x/mg-profile';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 py-2 ${isHiddenProfile ? 'bg-transparent shadow-none' : 'bg-white shadow-md'}`}>
      <div className={`container mx-auto px-4 ${isHiddenProfile ? 'relative min-h-10' : 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 md:gap-4'}`}>
        {/* Logo — left, unchanged */}
        {!isHiddenProfile && (
          <div className="flex min-w-0 items-center justify-start">
            <Link to="/" className="inline-flex min-w-0" aria-label="Samsons Enterprises home">
              <img
                src="https://i.postimg.cc/PrHK1VRs/logo-1.png"
                alt="Samsons Enterprises FZC Logo"
                className="h-10 max-w-full object-contain object-left"
              />
            </Link>
          </div>
        )}

        {/* Language switcher — slightly left of center without moving logo or navigation */}
        <div className={isHiddenProfile ? 'absolute top-0 right-3 flex items-center' : 'flex items-center justify-center md:-translate-x-5'}>
          <LanguageSwitcher />
        </div>

        {!isHiddenProfile && (
          <div className="flex min-w-0 items-center justify-end">
            {/* Desktop Navigation — right aligned */}
            <nav className="hidden md:flex items-center justify-end space-x-1 whitespace-nowrap" aria-label="Primary navigation">
              {navLinks.map(link => (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''} text-vista-gray hover:text-vista-green`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-md inline-flex items-center justify-center min-h-11 min-w-11"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      {!isHiddenProfile && isMobileMenuOpen && (
        <nav id="mobile-navigation" className="md:hidden bg-white py-4 px-4 shadow-md animate-fade-in" aria-label="Mobile navigation">
          <div className="flex flex-col space-y-3">
            {navLinks.map(link => (
              <Link
                key={link.id}
                to={link.path}
                className={`nav-link text-left py-2 ${isActive(link.path) ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
